import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, Search, Zap, Globe, Target, 
  RefreshCw, Download, Calendar, ArrowUp, ArrowDown,
  CheckCircle, AlertTriangle, XCircle, Activity,
  Gauge, Brain, Layers, Monitor, Smartphone, Clock,
  FileText, Mail, Settings, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Score Gauge Component
const ScoreGauge = ({ score, label, color, icon: Icon, description }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Poor';
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{label}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
      </div>
      
      {/* Circular Gauge */}
      <div className="relative w-32 h-32 mx-auto my-4">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="10"
          />
          {/* Score circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : score >= 40 ? '#f97316' : '#ef4444'}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 283} 283`}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{Math.round(score)}</span>
          <span className="text-xs text-gray-500">/ 100</span>
        </div>
      </div>
      
      {/* Score Label */}
      <div className="text-center">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreBg(score)} text-white`}>
          {getScoreLabel(score)}
        </span>
      </div>
    </div>
  );
};

// Core Web Vital Card
const CoreWebVitalCard = ({ metric, value, category, unit, description }) => {
  const getCategoryColor = (cat) => {
    if (cat === 'FAST') return 'bg-green-100 text-green-800 border-green-200';
    if (cat === 'AVERAGE') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getCategoryIcon = (cat) => {
    if (cat === 'FAST') return <CheckCircle className="w-4 h-4" />;
    if (cat === 'AVERAGE') return <AlertTriangle className="w-4 h-4" />;
    return <XCircle className="w-4 h-4" />;
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{metric}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(category)}`}>
          {getCategoryIcon(category)}
          {category || 'N/A'}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-gray-900">{value || 'N/A'}</span>
        <span className="text-sm text-gray-500">{unit}</span>
      </div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
};

// Main SEO Dashboard Component
const SEODashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [seoReport, setSeoReport] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [error, setError] = useState(null);
  const [siteUrl, setSiteUrl] = useState('https://automateseo.ai');

  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  const fetchServiceStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/api/google-seo/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServiceStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch service status:', err);
    }
  }, [navigate]);

  const downloadPdfReport = useCallback(async () => {
    setDownloadingPdf(true);
    
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/api/google-seo/report/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_url: siteUrl,
          days: 30
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SEO_Report_${siteUrl.replace(/https?:\/\//, '').replace(/\//g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to download PDF report');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setDownloadingPdf(false);
    }
  }, [navigate, siteUrl]);

  const generateSEOReport = useCallback(async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/api/google-seo/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_url: siteUrl,
          days: 30
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSeoReport(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to generate report');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  }, [navigate, siteUrl]);

  useEffect(() => {
    fetchServiceStatus();
    generateSEOReport();
  }, [fetchServiceStatus, generateSEOReport]);

  const scores = seoReport?.seo_score || {
    seo: 0,
    aeo: 0,
    geo: 0,
    technical: 0,
    overall: 0
  };

  const pagespeedMobile = seoReport?.pagespeed?.mobile || {};
  const pagespeedDesktop = seoReport?.pagespeed?.desktop || {};
  const cwvMobile = pagespeedMobile?.core_web_vitals || {};
  const lighthouseMobile = pagespeedMobile?.lighthouse_scores || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <img src="/logo.png" alt="AutomateSEO" className="w-10 h-10 rounded-xl" />
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  SEO Dashboard
                </span>
              </div>
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-700">
                  {serviceStatus?.status === 'active' ? 'All APIs Active' : 'Limited Mode'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => generateSEOReport()}
                disabled={generating}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Analyzing...' : 'Refresh'}
              </Button>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-blue-600 to-purple-600"
                onClick={() => downloadPdfReport()}
                disabled={downloadingPdf}
              >
                <Download className={`w-4 h-4 mr-2 ${downloadingPdf ? 'animate-bounce' : ''}`} />
                {downloadingPdf ? 'Generating...' : 'Export PDF'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Site URL Input */}
        <div className="mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Globe className="w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                  placeholder="Enter your website URL"
                  className="flex-1 border-none outline-none text-lg font-medium"
                />
                <Button 
                  onClick={() => generateSEOReport()}
                  disabled={generating}
                >
                  {generating ? 'Analyzing...' : 'Analyze'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Analyzing your website...</p>
              <p className="text-sm text-gray-400">This may take a moment</p>
            </div>
          </div>
        ) : (
          <>
            {/* Overall Score Banner */}
            <div className="mb-8">
              <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Overall SEO Health Score</h2>
                      <p className="opacity-90">Combined score from SEO, AEO, GEO & Technical analysis</p>
                      <div className="flex items-center gap-4 mt-4">
                        <span className="text-6xl font-bold">{Math.round(scores.overall)}</span>
                        <span className="text-2xl opacity-75">/ 100</span>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <div className="w-40 h-40 rounded-full border-8 border-white/30 flex items-center justify-center">
                        <Gauge className="w-20 h-20 opacity-50" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <ScoreGauge
                score={scores.seo}
                label="SEO Score"
                color="bg-gradient-to-br from-blue-500 to-blue-600"
                icon={Search}
                description="Search Engine Optimization"
              />
              <ScoreGauge
                score={scores.aeo}
                label="AEO Score"
                color="bg-gradient-to-br from-purple-500 to-purple-600"
                icon={Brain}
                description="Answer Engine Optimization"
              />
              <ScoreGauge
                score={scores.geo}
                label="GEO Score"
                color="bg-gradient-to-br from-green-500 to-green-600"
                icon={Layers}
                description="Generative Engine Optimization"
              />
              <ScoreGauge
                score={scores.technical}
                label="Technical Score"
                color="bg-gradient-to-br from-orange-500 to-orange-600"
                icon={Zap}
                description="Core Web Vitals & Performance"
              />
            </div>

            {/* Core Web Vitals Section */}
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Activity className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <CardTitle>Core Web Vitals</CardTitle>
                        <CardDescription>Real user experience metrics from Google</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="gap-2">
                        <Smartphone className="w-4 h-4" />
                        Mobile
                      </Button>
                      <Button variant="ghost" size="sm" className="gap-2">
                        <Monitor className="w-4 h-4" />
                        Desktop
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <CoreWebVitalCard
                      metric="LCP"
                      value={cwvMobile?.lcp?.value}
                      category={cwvMobile?.lcp?.category}
                      unit="ms"
                      description="Largest Contentful Paint"
                    />
                    <CoreWebVitalCard
                      metric="FID"
                      value={cwvMobile?.fid?.value}
                      category={cwvMobile?.fid?.category}
                      unit="ms"
                      description="First Input Delay"
                    />
                    <CoreWebVitalCard
                      metric="CLS"
                      value={cwvMobile?.cls?.value ? (cwvMobile.cls.value / 100).toFixed(3) : 'N/A'}
                      category={cwvMobile?.cls?.category}
                      unit=""
                      description="Cumulative Layout Shift"
                    />
                    <CoreWebVitalCard
                      metric="FCP"
                      value={cwvMobile?.fcp?.value}
                      category={cwvMobile?.fcp?.category}
                      unit="ms"
                      description="First Contentful Paint"
                    />
                    <CoreWebVitalCard
                      metric="INP"
                      value={cwvMobile?.inp?.value}
                      category={cwvMobile?.inp?.category}
                      unit="ms"
                      description="Interaction to Next Paint"
                    />
                    <CoreWebVitalCard
                      metric="TTFB"
                      value={cwvMobile?.ttfb?.value}
                      category={cwvMobile?.ttfb?.category}
                      unit="ms"
                      description="Time to First Byte"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lighthouse Scores */}
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Lighthouse Scores</CardTitle>
                      <CardDescription>Google Lighthouse audit results</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                      { label: 'Performance', score: lighthouseMobile?.performance, color: 'blue' },
                      { label: 'Accessibility', score: lighthouseMobile?.accessibility, color: 'purple' },
                      { label: 'Best Practices', score: lighthouseMobile?.best_practices, color: 'green' },
                      { label: 'SEO', score: lighthouseMobile?.seo, color: 'orange' }
                    ].map((item, index) => (
                      <div key={index} className="text-center">
                        <div className={`w-20 h-20 mx-auto rounded-full border-4 flex items-center justify-center mb-2 ${
                          (item.score || 0) >= 90 ? 'border-green-500 text-green-500' :
                          (item.score || 0) >= 50 ? 'border-yellow-500 text-yellow-500' :
                          'border-red-500 text-red-500'
                        }`}>
                          <span className="text-2xl font-bold">{Math.round(item.score || 0)}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-700">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* AEO Explanation */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Brain className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle>AEO - Answer Engine Optimization</CardTitle>
                      <CardDescription>Featured Snippet & Voice Search Readiness</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Featured Snippet Potential</span>
                      <span className="font-medium">{scores.aeo >= 60 ? 'High' : scores.aeo >= 40 ? 'Medium' : 'Low'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Voice Search Ready</span>
                      <span className={`font-medium ${scores.aeo >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                        {scores.aeo >= 50 ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Question-Based Content</span>
                      <span className="font-medium">{scores.aeo >= 70 ? 'Optimized' : 'Needs Work'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* GEO Explanation */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <Layers className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>GEO - Generative Engine Optimization</CardTitle>
                      <CardDescription>AI Overview & ChatGPT Visibility</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">AI Overview Eligible</span>
                      <span className={`font-medium ${scores.geo >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                        {scores.geo >= 50 ? 'Yes' : 'Unlikely'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Entity Recognition</span>
                      <span className="font-medium">{scores.geo >= 60 ? 'Strong' : scores.geo >= 40 ? 'Moderate' : 'Weak'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">AI Citation Potential</span>
                      <span className="font-medium">{scores.geo >= 70 ? 'High' : scores.geo >= 40 ? 'Medium' : 'Low'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Manage your SEO automation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/dashboard')}>
                    <BarChart3 className="w-6 h-6" />
                    <span>Full Dashboard</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                    <FileText className="w-6 h-6" />
                    <span>View Reports</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2">
                    <Settings className="w-6 h-6" />
                    <span>Settings</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Report Info */}
            {seoReport && (
              <div className="mt-6 text-center text-sm text-gray-500">
                <p>
                  Report generated at {new Date(seoReport.generated_at).toLocaleString()} • 
                  Analyzing {seoReport.date_range?.days || 30} days of data
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SEODashboard;
